import { UpgradeSubscriptionModal } from '@components'

import { useModal } from '@billy/design-system'
import { Button, Icon, Menu } from '@shinetools/lumen-react'
import { ArrowDownToLine, ArrowUpFromLine, EllipsisIcon, FileText, Trash2 } from 'lucide-react'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DeleteContactConfirmationModal } from '@features/contacts/elements/DeleteContactConfirmationModal'
import { Contact } from '@features/contacts/types/contact'

import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { Side } from '../../../../enums/side'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useComponentId } from '../../../../hooks/useComponentId'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { SupplierPaymentModal } from '../../routes/SupplierDetail/elements/HeaderSupplierDetail/elements/HeaderSupplierDetailActions/elements/HeaderSupplierDetailMenu/elements/SupplierPaymentModal'
import { SupplierStatementExportModal } from '../../routes/SupplierDetail/elements/HeaderSupplierDetail/elements/HeaderSupplierDetailActions/elements/HeaderSupplierDetailMenu/elements/SupplierStatementExportModal'

type SupplierActionsModal = 'delete' | 'payment' | 'statement'

export interface SupplierActionsMenuProps {
  contact: Pick<Contact, 'id' | 'name' | 'organizationId'>
  onDeleteContactSuccess?: (contactId: string) => void
  placement?: 'bottom-end' | 'bottom-start'
  triggerVariant?: 'outlined' | 'text'
}

export const SupplierActionsMenu = ({
  contact,
  onDeleteContactSuccess,
  placement = 'bottom-start',
  triggerVariant = 'outlined',
}: SupplierActionsMenuProps): ReactElement => {
  const { t } = useTranslation()
  const [paymentSide, setPaymentSide] = useState(Side.Debit)
  // One SupplierActionsMenu is mounted per table row, but the three modals below register
  // under fixed, shared ModalId values. withModalConditionalRender keys off that same global
  // id, so opening one row's modal would flip every row's modal open too, each still bound to
  // its own `contact` prop. Mounting only the modal the active row opened keeps the rest
  // unregistered until clicked.
  const [activeModal, setActiveModal] = useState<SupplierActionsModal | null>(null)

  const handleModalClose = useCallback(() => {
    setActiveModal(null)
  }, [])

  const { open: openPaymentModal } = useModal(ModalId.SupplierPaymentModal, { onClose: handleModalClose })
  const { open: openStatementExportModal } = useModal(ModalId.SupplierStatementExportModal, {
    onClose: handleModalClose,
  })
  const { open: openDeleteModal } = useModal(ModalId.DeleteContactConfirmationModal, { onClose: handleModalClose })
  // Unlike the other three modals above, the upgrade modal isn't gated by activeModal: it already
  // gets a unique id per row from useComponentId, so mounting it unconditionally can't collide
  // with another row's instance the way a shared ModalId would.
  const upgradeSubscriptionModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeSubscriptionModal } = useModal(upgradeSubscriptionModalId)

  const handleIncomingPaymentClick = useCallback(() => {
    setPaymentSide(Side.Debit)
    setActiveModal('payment')
    openPaymentModal()
  }, [openPaymentModal])

  const handleOutgoingPaymentClick = useCallback(() => {
    setPaymentSide(Side.Credit)
    setActiveModal('payment')
    openPaymentModal()
  }, [openPaymentModal])

  const handleStatementClick = useCallback(() => {
    if (!isAuthorized(Scope.ContactStatementRead)) {
      openUpgradeSubscriptionModal()
      return
    }

    setActiveModal('statement')
    openStatementExportModal()
  }, [openStatementExportModal, openUpgradeSubscriptionModal])

  const handleDeleteClick = useCallback(() => {
    setActiveModal('delete')
    openDeleteModal()
  }, [openDeleteModal])

  const handleDeleteContactSuccess = useCallback(
    (contactId: string) => {
      onDeleteContactSuccess?.(contactId)
    },
    [onDeleteContactSuccess],
  )

  return (
    <>
      <Menu
        aria-label={t('more')}
        placement={placement}
        size="medium"
        trigger={<Button aria-label={t('more')} icon={<Icon icon={EllipsisIcon} />} variant={triggerVariant} />}
        width="hug"
      >
        <Menu.Item
          icon={<Icon icon={ArrowDownToLine} size="small" />}
          label={t('contact.index.payment.ingoing')}
          onClick={handleIncomingPaymentClick}
        />
        <Menu.Item
          icon={<Icon icon={ArrowUpFromLine} size="small" />}
          label={t('contact.index.payment.outgoing')}
          onClick={handleOutgoingPaymentClick}
        />
        <Menu.Item
          icon={<Icon icon={FileText} size="small" />}
          label={t('contact.index.account_statement')}
          onClick={handleStatementClick}
        />
        <Menu.Item
          destructive
          icon={<Icon icon={Trash2} size="small" />}
          label={t('delete')}
          onClick={handleDeleteClick}
        />
      </Menu>
      {activeModal === 'payment' && (
        <SupplierPaymentModal contact={contact} id={ModalId.SupplierPaymentModal} side={paymentSide} />
      )}
      {activeModal === 'statement' && (
        <SupplierStatementExportModal contact={contact} id={ModalId.SupplierStatementExportModal} />
      )}
      {activeModal === 'delete' && (
        <DeleteContactConfirmationModal
          onDeleteContactSuccess={handleDeleteContactSuccess}
          selectedContact={{ id: contact.id, name: contact.name }}
          variant="contactsList"
        />
      )}
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        trackingContext={TrackingContext.SupplierAccountStatement}
      />
    </>
  )
}
